import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Download, Upload, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import {
  ALL_DATA_MODULES,
  type DataModuleId,
  type ExportBundleV1,
  accountDataExport,
  accountDataImportPreflight,
  accountDataImportApply,
  type ImportMode,
} from '@/api/dataExport';
import { notifications } from '@/utils/notifications';

const MODULE_LABEL_KEYS: Record<DataModuleId, string> = {
  groups: 'importExport.moduleLabels.groups',
  tags: 'importExport.moduleLabels.tags',
  troves: 'importExport.moduleLabels.troves',
  notes: 'importExport.moduleLabels.notes',
  highlighter: 'importExport.moduleLabels.highlighter',
  session: 'importExport.moduleLabels.session',
  sessionbookmark: 'importExport.moduleLabels.sessionbookmark',
  tasks: 'importExport.moduleLabels.tasks',
  tabs: 'importExport.moduleLabels.tabs',
  preference: 'importExport.moduleLabels.preference',
  shares: 'importExport.moduleLabels.shares',
  aiChunks: 'importExport.moduleLabels.aiChunks',
  aiUsageEvents: 'importExport.moduleLabels.aiUsageEvents',
};

function downloadJson(bundle: ExportBundleV1) {
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `trove-export-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export const ImportExportSettings = () => {
  const { t } = useTranslation('settings');
  const { t: tToast } = useTranslation('notifications');
  const [selected, setSelected] = useState<Set<DataModuleId>>(() => new Set(ALL_DATA_MODULES));
  const [loading, setLoading] = useState(false);
  const [bundle, setBundle] = useState<ExportBundleV1 | null>(null);
  const [preflightSummary, setPreflightSummary] = useState<Record<string, { approxDocs?: number; note?: string }> | null>(
    null
  );
  const [preflightWarnings, setPreflightWarnings] = useState<string[]>([]);
  const [importMode, setImportMode] = useState<ImportMode>('merge');
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [applyReport, setApplyReport] = useState<Record<string, { created: number; skipped: number; errors: string[] }> | null>(
    null
  );

  const toggleModule = (id: DataModuleId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(ALL_DATA_MODULES));
  const selectNone = () => setSelected(new Set());

  const modulesList = useMemo(() => Array.from(selected), [selected]);

  const handleExport = async () => {
    if (modulesList.length === 0) {
      notifications.error(tToast('importExport.selectModuleExport'));
      return;
    }
    setLoading(true);
    setApplyReport(null);
    try {
      const b = await accountDataExport(modulesList);
      downloadJson(b);
      notifications.success(tToast('importExport.exportDownloaded'));
    } catch (e) {
      notifications.error(e instanceof Error ? e.message : tToast('importExport.exportFailed'));
    } finally {
      setLoading(false);
    }
  };

  const onFile = useCallback((file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as ExportBundleV1;
        if (parsed.version !== 1 || !parsed.data || !Array.isArray(parsed.modules)) {
          throw new Error(t('importExport.invalidFileError'));
        }
        setBundle(parsed);
        setPreflightSummary(null);
        setPreflightWarnings([]);
        setApplyReport(null);
        setSelected(new Set(parsed.modules.filter((m) => ALL_DATA_MODULES.includes(m as DataModuleId)) as DataModuleId[]));
        notifications.success(tToast('importExport.bundleLoaded'));
      } catch (e) {
        notifications.error(e instanceof Error ? e.message : tToast('importExport.readFileFailed'));
        setBundle(null);
      }
    };
    reader.readAsText(file);
  }, [tToast]);

  const handlePreflight = async () => {
    if (!bundle) {
      notifications.error(tToast('importExport.chooseFileFirst'));
      return;
    }
    if (modulesList.length === 0) {
      notifications.error(tToast('importExport.selectModuleImport'));
      return;
    }
    setLoading(true);
    setApplyReport(null);
    try {
      const res = await accountDataImportPreflight(bundle, modulesList);
      if (res.status !== 'success') {
        notifications.error(res.message ?? tToast('importExport.preflightFailed'));
        return;
      }
      setPreflightSummary(res.summary ?? null);
      setPreflightWarnings(res.warnings ?? []);
      notifications.success(tToast('importExport.preflightOk'));
    } catch (e) {
      notifications.error(e instanceof Error ? e.message : tToast('importExport.preflightFailedGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!bundle) {
      notifications.error(tToast('importExport.chooseFileFirst'));
      return;
    }
    if (modulesList.length === 0) {
      notifications.error(tToast('importExport.selectModuleImport'));
      return;
    }
    if (importMode === 'replace' && !confirmReplace) {
      notifications.error(tToast('importExport.confirmReplace'));
      return;
    }
    setLoading(true);
    try {
      const res = await accountDataImportApply(bundle, modulesList, importMode, importMode === 'replace' ? true : false);
      if (res.status !== 'success') {
        notifications.error((res as { message?: string }).message ?? tToast('importExport.importFailed'));
        return;
      }
      setApplyReport(
        res.report
          ? Object.fromEntries(
              Object.entries(res.report).map(([k, v]) => [
                k,
                { created: v.created, skipped: v.skipped, errors: v.errors },
              ])
            )
          : null
      );
      notifications.success(tToast('importExport.importCompleted'));
    } catch (e) {
      notifications.error(e instanceof Error ? e.message : tToast('importExport.importFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6 pb-8">
      <header className="space-y-1">
        <h2 className="text-2xl font-bold tracking-tight text-foreground">{t('importExport.title')}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">{t('importExport.description')}</p>
      </header>

      <section className="rounded-2xl bg-card p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-foreground">{t('importExport.modules')}</h3>
          <div className="flex items-center gap-2 text-xs">
            <button type="button" onClick={selectAll} className="font-semibold text-primary hover:underline">
              {t('importExport.selectAll')}
            </button>
            <span className="text-muted-foreground">·</span>
            <button type="button" onClick={selectNone} className="font-semibold text-muted-foreground hover:underline">
              {t('importExport.clear')}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {ALL_DATA_MODULES.map((id) => (
            <label
              key={id}
              className={clsx(
                'flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 transition-colors',
                selected.has(id) ? 'bg-primary/10 ring-1 ring-primary/30' : 'bg-muted/40 hover:bg-muted/60'
              )}
            >
              <input
                type="checkbox"
                checked={selected.has(id)}
                onChange={() => toggleModule(id)}
                className="rounded border-border-muted"
              />
              <span className="text-sm font-medium text-foreground">{t(MODULE_LABEL_KEYS[id])}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-4 rounded-2xl bg-card p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
              <Download className="h-4 w-4" />
            </span>
            <h3 className="text-sm font-bold text-foreground">{t('importExport.exportTitle')}</h3>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">{t('importExport.exportDescription')}</p>
          <button
            type="button"
            disabled={loading}
            onClick={handleExport}
            className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {t('importExport.downloadExport')}
          </button>
        </div>

        <div className="space-y-4 rounded-2xl bg-card p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
              <Upload className="h-4 w-4" />
            </span>
            <h3 className="text-sm font-bold text-foreground">{t('importExport.importTitle')}</h3>
          </div>

          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border-muted bg-muted/20 px-4 py-6 transition-colors hover:bg-muted/30">
            <Upload className="h-5 w-5 text-muted-foreground" aria-hidden />
            <span className="text-xs font-medium text-muted-foreground">
              {bundle
                ? t('importExport.loadedBundle', {
                    count: bundle.modules.length,
                    exportedAt: new Date(bundle.exportedAt).toLocaleString(),
                  })
                : t('importExport.chooseFile')}
            </span>
            <input
              type="file"
              accept="application/json,.json"
              className="sr-only"
              onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            />
          </label>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">{t('importExport.importMode')}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setImportMode('merge')}
                className={clsx(
                  'flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors',
                  importMode === 'merge'
                    ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted/70'
                )}
              >
                {t('importExport.mergeMode')}
              </button>
              <button
                type="button"
                onClick={() => setImportMode('replace')}
                className={clsx(
                  'flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors',
                  importMode === 'replace'
                    ? 'bg-destructive/10 text-destructive ring-1 ring-destructive/30'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted/70'
                )}
              >
                {t('importExport.replaceMode')}
              </button>
            </div>
            {importMode === 'replace' && (
              <label className="flex items-start gap-2 text-xs font-medium text-destructive">
                <input
                  type="checkbox"
                  checked={confirmReplace}
                  onChange={(e) => setConfirmReplace(e.target.checked)}
                  className="mt-0.5"
                />
                {t('importExport.replaceWarning')}
              </label>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={loading || !bundle}
              onClick={handlePreflight}
              className="min-h-10 rounded-xl bg-muted/50 px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-muted/70 disabled:opacity-50"
            >
              {t('importExport.runPreflight')}
            </button>
            <button
              type="button"
              disabled={loading || !bundle}
              onClick={handleApply}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('importExport.applyImport')}
            </button>
          </div>
        </div>
      </section>

      {preflightWarnings.length > 0 && (
        <div className="flex gap-2.5 rounded-xl bg-amber-500/5 p-4 text-sm text-amber-800 ring-1 ring-amber-500/25 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <ul className="list-disc space-y-1 pl-4">
            {preflightWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {preflightSummary && Object.keys(preflightSummary).length > 0 && (
        <div className="space-y-2 rounded-2xl bg-card p-4 sm:p-5">
          <p className="text-xs font-semibold text-muted-foreground">{t('importExport.preflightSummary')}</p>
          <ul className="space-y-1 text-sm">
            {Object.entries(preflightSummary).map(([k, v]) => (
              <li key={k}>
                <span className="font-semibold text-foreground">
                  {t(MODULE_LABEL_KEYS[k as DataModuleId] ?? '') || k}:
                </span>{' '}
                {v.note ?? t('importExport.documentsCount', { count: v.approxDocs ?? 0 })}
              </li>
            ))}
          </ul>
        </div>
      )}

      {applyReport && (
        <div className="space-y-3 rounded-2xl bg-card p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            <p className="text-sm font-bold text-foreground">{t('importExport.importReport')}</p>
          </div>
          <ul className="space-y-2 text-sm">
            {Object.entries(applyReport).map(([k, v]) => (
              <li key={k} className="border-b border-border-muted pb-2 last:border-0">
                <span className="font-semibold">{t(MODULE_LABEL_KEYS[k as DataModuleId] ?? '') || k}</span>:{' '}
                {t('importExport.importReportLine', { created: v.created, skipped: v.skipped })}
                {v.errors.length > 0 && (
                  <span className="mt-1 block text-xs text-destructive">{v.errors.slice(0, 3).join(' · ')}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
